import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardPermissions } from '@/hooks/useDashboardPermissions';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Headphones, Plus, AlertTriangle, CheckCircle2, Clock, ArrowUpRight, MessageCircle, ShieldAlert, Loader2 } from 'lucide-react';
import { ContactSupportForm } from '@/components/support/ContactSupportForm';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { formatDistanceToNow } from 'date-fns';

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700 border-blue-200',
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  in_progress: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  escalated: 'bg-red-100 text-red-700 border-red-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  closed: 'bg-gray-100 text-gray-600 border-gray-200',
};
const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-700',
  normal: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

export default function SupportTickets() {
  const { user } = useAuth();
  const { permissions: p } = useDashboardPermissions();
  const isStaff = p.canHandleReports || p.canManageLeadMarketplace;
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['support-tickets', isStaff, statusFilter],
    queryFn: async () => {
      let q = supabase.from('support_tickets' as any).select('*').order('created_at', { ascending: false }).limit(200);
      if (!isStaff && user) q = q.eq('user_id', user.id);
      if (statusFilter !== 'all') q = q.eq('status', statusFilter);
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
    enabled: !!user,
  });

  const filtered = useMemo(() => {
    if (!tickets) return [];
    if (!search.trim()) return tickets;
    const s = search.toLowerCase();
    return tickets.filter(t =>
      t.subject?.toLowerCase().includes(s) ||
      t.ticket_number?.toLowerCase().includes(s) ||
      t.email?.toLowerCase().includes(s) ||
      t.name?.toLowerCase().includes(s)
    );
  }, [tickets, search]);

  const counts = useMemo(() => {
    const c = { all: 0, open: 0, in_progress: 0, escalated: 0, resolved: 0 };
    (tickets || []).forEach(t => {
      c.all++;
      if (t.status in c) (c as any)[t.status]++;
    });
    return c;
  }, [tickets]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Headphones className="h-6 w-6 text-primary" />
            {isStaff ? 'Support Tickets Queue' : 'My Support Tickets'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isStaff
              ? 'Triage, assign, escalate and resolve customer issues.'
              : 'Track requests you have submitted and follow up with our team.'}
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button><Plus className="h-4 w-4 mr-1" />New ticket</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create a new ticket</DialogTitle></DialogHeader>
            <ContactSupportForm onSubmitted={() => qc.invalidateQueries({ queryKey: ['support-tickets'] })} />
          </DialogContent>
        </Dialog>
      </div>

      {isStaff && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { k: 'all', label: 'All', icon: MessageCircle, color: 'text-gray-600' },
            { k: 'open', label: 'Open', icon: Clock, color: 'text-blue-600' },
            { k: 'in_progress', label: 'In progress', icon: ArrowUpRight, color: 'text-indigo-600' },
            { k: 'escalated', label: 'Escalated', icon: ShieldAlert, color: 'text-red-600' },
            { k: 'resolved', label: 'Resolved', icon: CheckCircle2, color: 'text-emerald-600' },
          ].map(s => (
            <Card key={s.k} className={`cursor-pointer hover:shadow-md transition ${statusFilter === s.k ? 'ring-2 ring-primary' : ''}`}
              onClick={() => setStatusFilter(s.k)}>
              <CardContent className="p-3 flex items-center gap-2">
                <s.icon className={`h-5 w-5 ${s.color}`} />
                <div>
                  <p className="text-xl font-bold leading-none">{(counts as any)[s.k] || 0}</p>
                  <p className="text-[11px] text-muted-foreground">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3">
        <Input placeholder="Search by ticket #, subject, email..." value={search} onChange={e => setSearch(e.target.value)} className="max-w-md" />
        {!isStaff && (
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList>
              <TabsTrigger value="all">All</TabsTrigger>
              <TabsTrigger value="open">Open</TabsTrigger>
              <TabsTrigger value="resolved">Resolved</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground">
          No tickets {search ? 'match your search' : 'yet'}.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(t => (
            <Card key={t.id} className="cursor-pointer hover:border-primary transition" onClick={() => setOpenId(t.id)}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-muted-foreground">{t.ticket_number}</span>
                    <Badge variant="outline" className={STATUS_COLORS[t.status] || ''}>{t.status.replace('_', ' ')}</Badge>
                    <Badge variant="outline" className={PRIORITY_COLORS[t.priority] || ''}>{t.priority}</Badge>
                    {t.escalated_at && <AlertTriangle className="h-3.5 w-3.5 text-red-500" />}
                  </div>
                  <p className="font-semibold text-sm mt-1 truncate">{t.subject}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {isStaff ? `${t.name} · ${t.email} · ` : ''}{formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TicketDetailSheet ticketId={openId} onClose={() => setOpenId(null)} isStaff={isStaff} />
    </div>
  );
}

function TicketDetailSheet({ ticketId, onClose, isStaff }: { ticketId: string | null; onClose: () => void; isStaff: boolean }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [posting, setPosting] = useState(false);

  const { data: ticket } = useQuery({
    queryKey: ['support-ticket', ticketId],
    queryFn: async () => {
      const { data } = await supabase.from('support_tickets' as any).select('*').eq('id', ticketId!).single();
      return data as any;
    },
    enabled: !!ticketId,
  });
  const { data: messages } = useQuery({
    queryKey: ['support-ticket-msgs', ticketId],
    queryFn: async () => {
      const { data } = await supabase.from('support_ticket_messages' as any)
        .select('*').eq('ticket_id', ticketId!).order('created_at', { ascending: true });
      return (data || []) as any[];
    },
    enabled: !!ticketId,
  });

  useEffect(() => { setReply(''); setInternal(false); }, [ticketId]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['support-ticket', ticketId] });
    qc.invalidateQueries({ queryKey: ['support-ticket-msgs', ticketId] });
    qc.invalidateQueries({ queryKey: ['support-tickets'] });
  };

  const sendReply = async () => {
    if (!reply.trim() || !ticketId || !user) return;
    setPosting(true);
    try {
      const { error } = await supabase.from('support_ticket_messages' as any).insert({
        ticket_id: ticketId,
        author_id: user.id,
        author_type: isStaff ? 'agent' : 'customer',
        body: reply.trim(),
        internal: isStaff ? internal : false,
      });
      if (error) throw error;
      // Update first_response_at + status
      if (isStaff && ticket && !ticket.first_response_at && !internal) {
        await supabase.from('support_tickets' as any).update({
          first_response_at: new Date().toISOString(),
          status: ticket.status === 'open' ? 'in_progress' : ticket.status,
        }).eq('id', ticketId);
      }
      setReply('');
      refresh();
      toast.success('Reply sent');
    } catch (e: any) { toast.error(e?.message || 'Failed to send'); }
    finally { setPosting(false); }
  };

  const updateStatus = async (status: string, extra: any = {}) => {
    if (!ticketId) return;
    const { error } = await supabase.from('support_tickets' as any).update({
      status,
      ...(status === 'resolved' ? { resolved_at: new Date().toISOString() } : {}),
      ...(status === 'closed' ? { closed_at: new Date().toISOString() } : {}),
      ...extra,
    }).eq('id', ticketId);
    if (error) toast.error(error.message);
    else { toast.success(`Ticket ${status}`); refresh(); }
  };

  const escalate = async () => {
    if (!ticketId) return;
    const reason = window.prompt('Escalation reason?') || 'Manual escalation';
    await supabase.from('support_tickets' as any).update({
      status: 'escalated', escalated_at: new Date().toISOString(), escalation_reason: reason,
    }).eq('id', ticketId);
    toast.success('Ticket escalated');
    refresh();
  };

  return (
    <Sheet open={!!ticketId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col p-0">
        {ticket && (
          <>
            <SheetHeader className="p-5 border-b">
              <SheetTitle className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm text-muted-foreground">{ticket.ticket_number}</span>
                <Badge variant="outline" className={STATUS_COLORS[ticket.status]}>{ticket.status.replace('_', ' ')}</Badge>
                <Badge variant="outline" className={PRIORITY_COLORS[ticket.priority]}>{ticket.priority}</Badge>
              </SheetTitle>
              <p className="font-semibold text-base">{ticket.subject}</p>
              <p className="text-xs text-muted-foreground">
                From <strong>{ticket.name}</strong> · {ticket.email} {ticket.phone ? `· ${ticket.phone}` : ''}
              </p>
              <p className="text-xs text-muted-foreground">Category: {ticket.category}</p>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-muted/20">
              {(messages || []).map(m => (
                <div key={m.id} className={`p-3 rounded-lg border max-w-[90%] ${
                  m.internal ? 'bg-amber-50 border-amber-200' :
                  m.author_type === 'agent' ? 'bg-primary/5 border-primary/20 ml-auto' : 'bg-card'
                }`}>
                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground mb-1">
                    <span className="font-semibold capitalize">{m.author_type}</span>
                    {m.internal && <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">Internal</Badge>}
                    <span>· {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}</span>
                  </div>
                  <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                </div>
              ))}
            </div>

            {!['closed'].includes(ticket.status) && (
              <div className="p-4 border-t space-y-2">
                <Textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." rows={3} />
                <div className="flex items-center justify-between gap-2">
                  {isStaff ? (
                    <div className="flex items-center gap-2">
                      <Switch id="internal" checked={internal} onCheckedChange={setInternal} />
                      <Label htmlFor="internal" className="text-xs">Internal note</Label>
                    </div>
                  ) : <span />}
                  <Button onClick={sendReply} disabled={posting || !reply.trim()} size="sm">
                    {posting && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                    Send reply
                  </Button>
                </div>
                {isStaff && (
                  <div className="flex flex-wrap gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => updateStatus('in_progress')}>In progress</Button>
                    <Button size="sm" variant="outline" onClick={escalate}>Escalate</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus('resolved')}>Resolve</Button>
                    <Button size="sm" variant="outline" onClick={() => updateStatus('closed')}>Close</Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
